import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import type { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { StreetViewManifest } from "../types.ts";
import { PinholeBlendSource } from "./PinholeBlendSource.ts";
import { PanoramaSession } from "./PanoramaSession.ts";
import type { PanoramaNode } from "./PanoramaSource.ts";

const MARKER_RADIUS = 0.035;
const MARKER_COLOR = 0x4a9eff;
const MARKER_COLOR_HOVER = 0x6ab4ff;
const MARKER_COLOR_ACTIVE = 0xffaa4a;
const CROSSFADE_MS = 350;
const MIN_PANEL_W = 280;

/**
 * RTABmap pinhole street view: 3D markers on the floor plan, click to open
 * a side photo panel, and (optionally) a panorama session that blends 8
 * neighbouring pinhole frames into a sphere.
 *
 * Panorama rendering is delegated to {@link PanoramaSession} +
 * {@link PinholeBlendSource}. Panel + markers stay here.
 */
export class StreetView {
  // Data
  private manifest: StreetViewManifest | null = null;
  private nodes = new Map<number, PanoramaNode>();
  private adjacency = new Map<number, number[]>();
  private sortedNodeIds: number[] = [];
  private imageBaseUrl = "";

  // Panorama subsystem
  private source: PinholeBlendSource | null = null;
  private session: PanoramaSession | null = null;

  // 3D markers
  private markersGroup: THREE.Group;
  private labelsGroup: THREE.Group;
  private markerMeshes = new Map<number, THREE.Mesh>();
  private debugLabels: CSS2DObject[] = [];
  private sharedGeometry: THREE.SphereGeometry;
  private sharedMaterial: THREE.MeshBasicMaterial;
  private hoverMaterial: THREE.MeshBasicMaterial;
  private activeMaterial: THREE.MeshBasicMaterial;
  private raycaster = new THREE.Raycaster();

  // Panel DOM (photo mode)
  private panel: HTMLElement;
  private canvasContainer: HTMLElement;
  private photoA: HTMLImageElement;
  private photoB: HTMLImageElement;
  private navContainer: HTMLElement;
  private infoLabel: HTMLElement;
  private dragHandle: HTMLElement;

  // State
  private currentNodeId = -1;
  private active = false;
  private activePhoto: "a" | "b" = "a";
  private transitioning = false;
  private hoveredMarker: THREE.Mesh | null = null;
  private activeMarker: THREE.Mesh | null = null;

  // Callbacks
  onEnter: ((nodeId: number, position: THREE.Vector3) => void) | null = null;
  onExit: ((nodeId: number, position: THREE.Vector3) => void) | null = null;
  onPanelResize: ((panelWidth: number) => void) | null = null;
  onPanoExit: (() => void) | null = null;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private controls: OrbitControls,
    private planRoot: THREE.Group,
    _meshes: THREE.Mesh[], // kept for API compat, unused now
  ) {
    const canvas = this.renderer.domElement;
    this.canvasContainer = document.getElementById("canvas-container")!;

    this.sharedGeometry = new THREE.SphereGeometry(MARKER_RADIUS, 12, 12);
    this.sharedMaterial = new THREE.MeshBasicMaterial({ color: MARKER_COLOR, transparent: true, opacity: 0.85, depthTest: false });
    this.hoverMaterial = new THREE.MeshBasicMaterial({ color: MARKER_COLOR_HOVER, transparent: true, opacity: 1, depthTest: false });
    this.activeMaterial = new THREE.MeshBasicMaterial({ color: MARKER_COLOR_ACTIVE, transparent: true, opacity: 1, depthTest: false });

    this.markersGroup = new THREE.Group();
    this.markersGroup.renderOrder = 998;
    this.markersGroup.visible = false;
    this.planRoot.add(this.markersGroup);
    this.labelsGroup = new THREE.Group();
    this.markersGroup.add(this.labelsGroup);

    // Panel DOM
    this.panel = document.getElementById("streetview-panel")!;
    this.photoA = this.panel.querySelector(".sv-photo-a") as HTMLImageElement;
    this.photoB = this.panel.querySelector(".sv-photo-b") as HTMLImageElement;
    this.navContainer = this.panel.querySelector(".sv-nav")!;
    this.infoLabel = this.panel.querySelector(".sv-info")!;
    this.dragHandle = this.panel.querySelector(".sv-drag")!;
    this.panel.querySelector(".sv-close")!.addEventListener("click", () => this.exit());

    // Sequential prev/next navigation
    this.panel.querySelector(".sv-prev")!.addEventListener("click", () => this._goSequential(-1));
    this.panel.querySelector(".sv-next")!.addEventListener("click", () => this._goSequential(+1));

    // Keyboard
    this._onKeyDown = this._onKeyDown.bind(this);

    // Canvas marker interactions
    canvas.addEventListener("mousemove", this._onMouseMove);
    canvas.addEventListener("click", this._onClick, true);

    this._initDrag();
  }

  get loaded(): boolean { return this.manifest !== null; }
  get isActive(): boolean { return this.active; }
  get isPanoActive(): boolean { return this.session?.isActive ?? false; }

  async loadManifest(manifestUrl: string, imageBaseUrl: string, modelOffset: THREE.Vector3): Promise<void> {
    const resp = await fetch(manifestUrl);
    if (!resp.ok) throw new Error(`Failed to load streetview manifest (HTTP ${resp.status})`);
    this.manifest = await resp.json();
    this.imageBaseUrl = imageBaseUrl;
    console.log(`[StreetView] manifest: ${this.manifest!.nodes.length} nodes, ${this.manifest!.links.length} links`);

    this.source = new PinholeBlendSource(this.manifest!, imageBaseUrl, modelOffset);
    this.session = new PanoramaSession(this.camera, this.renderer, this.scene, this.controls, this.planRoot, this.source);
    this.session.onExit = () => this._handlePanoExit();

    for (const n of this.source.getNodes()) this.nodes.set(n.id, n);
    for (const link of this.manifest!.links) {
      if (!this.adjacency.has(link.from)) this.adjacency.set(link.from, []);
      if (!this.adjacency.has(link.to)) this.adjacency.set(link.to, []);
      this.adjacency.get(link.from)!.push(link.to);
      this.adjacency.get(link.to)!.push(link.from);
    }
    this.sortedNodeIds = [...this.nodes.keys()].sort((a, b) => a - b);

    this._createMarkers();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Photo panel mode
  // ═══════════════════════════════════════════════════════════════════════════

  enter(nodeId: number): void {
    if (!this.manifest || !this.nodes.has(nodeId)) return;
    this.active = true;
    this.panel.classList.add("open");
    this._applyCanvasInset();
    document.addEventListener("keydown", this._onKeyDown);
    this._showNode(nodeId, false);
    this.onEnter?.(nodeId, this.nodes.get(nodeId)!.position);
  }

  exit(): void {
    if (this.session?.isActive) { this.session.exit(); return; }
    if (!this.active) return;
    this.active = false;
    this.panel.classList.remove("open");
    this.canvasContainer.style.left = "0";
    this._clearActiveMarker();
    document.removeEventListener("keydown", this._onKeyDown);
    const pos = this.nodes.get(this.currentNodeId)?.position;
    if (pos) this.onExit?.(this.currentNodeId, pos);
  }

  setMarkersVisible(v: boolean): void { this.markersGroup.visible = v; }

  /** Open panorama mode at the closest node (or the panel's current node). */
  async enterPanorama(): Promise<void> {
    if (!this.session) return;
    if (this.session.isActive) return;

    let nodeId = this.currentNodeId;
    if (!this.active || nodeId < 0) {
      nodeId = this.session.findNearestToCamera();
      if (nodeId < 0) return;
      this.currentNodeId = nodeId;
    }

    // Hide panel visually; restored by _handlePanoExit if it was open.
    this.panel.classList.remove("open");
    this.canvasContainer.style.left = "0";

    await this.session.enter(nodeId);
  }

  dispose(): void {
    this.exit();
    const c = this.renderer.domElement;
    c.removeEventListener("mousemove", this._onMouseMove);
    c.removeEventListener("click", this._onClick, true);
    this.markersGroup.removeFromParent();
    this.sharedGeometry.dispose();
    this.sharedMaterial.dispose();
    this.hoverMaterial.dispose();
    this.activeMaterial.dispose();
    this.markerMeshes.clear();
    for (const lbl of this.debugLabels) lbl.removeFromParent();
    this.debugLabels = [];
    this.session?.dispose();
    this.source?.dispose();
  }

  // ── 3D markers ───────────────────────────────────────────────────────────────

  private _createMarkers(): void {
    for (const mesh of this.markerMeshes.values()) mesh.removeFromParent();
    for (const lbl of this.debugLabels) lbl.removeFromParent();
    this.markerMeshes.clear(); this.debugLabels = [];

    for (const [nodeId, node] of this.nodes) {
      const mesh = new THREE.Mesh(this.sharedGeometry, this.sharedMaterial);
      mesh.position.copy(node.position); mesh.userData.streetviewNodeId = nodeId;
      mesh.renderOrder = 998; this.markersGroup.add(mesh); this.markerMeshes.set(nodeId, mesh);

      if (import.meta.env.VITE_DEBUG === "true") {
        const angleDeg = Math.round(THREE.MathUtils.radToDeg(node.initialYaw));
        const div = document.createElement("div");
        div.style.cssText = "color:#4a9eff;font:bold 10px monospace;background:rgba(0,0,0,.6);padding:1px 3px;border-radius:2px;pointer-events:none;white-space:nowrap;";
        div.textContent = `#${nodeId} ${angleDeg}°`;
        const label = new CSS2DObject(div);
        label.position.copy(node.position); label.position.y += 0.06;
        this.labelsGroup.add(label); this.debugLabels.push(label);
      }
    }
    console.log(`[StreetView] created ${this.markerMeshes.size} markers`);
  }

  private _highlightActiveMarker(nodeId: number): void {
    this._clearActiveMarker();
    const mesh = this.markerMeshes.get(nodeId);
    if (mesh) { mesh.material = this.activeMaterial; mesh.scale.setScalar(2); this.activeMarker = mesh; }
  }

  private _clearActiveMarker(): void {
    if (!this.activeMarker) return;
    this.activeMarker.material = this.sharedMaterial; this.activeMarker.scale.setScalar(1);
    this.activeMarker = null;
  }

  // ── Canvas interactions (markers) ────────────────────────────────────────────

  private _onMouseMove = (e: MouseEvent): void => {
    if (!this.markersGroup.visible || this.session?.isActive) return;
    const mouse = this._ndcFromEvent(e);
    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.markersGroup.children, false);

    if (this.hoveredMarker && this.hoveredMarker !== this.activeMarker) {
      this.hoveredMarker.material = this.sharedMaterial; this.hoveredMarker.scale.setScalar(1);
    }
    this.hoveredMarker = null;
    if (hits.length > 0) {
      const m = hits[0].object as THREE.Mesh;
      if (m !== this.activeMarker) {
        m.material = this.hoverMaterial; m.scale.setScalar(1.5);
      }
      this.hoveredMarker = m; this.renderer.domElement.style.cursor = "pointer";
    } else { this.renderer.domElement.style.cursor = ""; }
  };

  private _onClick = (e: MouseEvent): void => {
    if (!this.markersGroup.visible || this.session?.isActive) return;
    const mouse = this._ndcFromEvent(e);
    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.markersGroup.children, false);
    if (hits.length > 0) {
      const nodeId = hits[0].object.userData.streetviewNodeId as number;
      if (nodeId !== undefined) {
        e.stopImmediatePropagation();
        this.active ? this._showNode(nodeId, true) : this.enter(nodeId);
      }
    }
  };

  private _ndcFromEvent(e: MouseEvent): THREE.Vector2 {
    const r = this.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
  }

  // ── Panel logic ──────────────────────────────────────────────────────────────

  private _showNode(nodeId: number, crossfade: boolean): void {
    this.currentNodeId = nodeId;
    const imgUrl = `${this.imageBaseUrl}/${nodeId}.jpg`;
    const seqIdx = this.sortedNodeIds.indexOf(nodeId);
    this.infoLabel.textContent = `Node ${seqIdx + 1} / ${this.sortedNodeIds.length}`;
    const prevBtn = this.panel.querySelector(".sv-prev") as HTMLButtonElement;
    const nextBtn = this.panel.querySelector(".sv-next") as HTMLButtonElement;
    prevBtn.disabled = seqIdx <= 0;
    nextBtn.disabled = seqIdx >= this.sortedNodeIds.length - 1;
    this._highlightActiveMarker(nodeId);
    const next = this.activePhoto === "a" ? this.photoB : this.photoA;
    const curr = this.activePhoto === "a" ? this.photoA : this.photoB;
    if (crossfade && !this.transitioning) {
      this.transitioning = true;
      next.onload = () => { next.style.opacity = "1"; curr.style.opacity = "0"; this.activePhoto = this.activePhoto === "a" ? "b" : "a"; setTimeout(() => { this.transitioning = false; }, CROSSFADE_MS); };
      next.src = imgUrl;
    } else if (!crossfade) { curr.src = imgUrl; curr.style.opacity = "1"; next.style.opacity = "0"; }
    this._renderPanelNav(nodeId);
    for (const nid of this.adjacency.get(nodeId) ?? []) new Image().src = `${this.imageBaseUrl}/${nid}.jpg`;
  }

  private _renderPanelNav(nodeId: number): void {
    this.navContainer.innerHTML = "";
    const neighbors = this.adjacency.get(nodeId) ?? [];
    const cp = this.nodes.get(nodeId)!.position;
    const sorted = neighbors
      .map((nid) => {
        const np = this.nodes.get(nid)?.position;
        return np ? { id: nid, angle: Math.atan2(np.x - cp.x, np.z - cp.z) } : null;
      })
      .filter((x): x is { id: number; angle: number } => x !== null)
      .sort((a, b) => a.angle - b.angle);
    for (const { id: nid, angle } of sorted) {
      const btn = document.createElement("button"); btn.className = "sv-nav-btn";
      const deg = THREE.MathUtils.radToDeg(-angle) + 180;
      btn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="transform:rotate(${deg}deg)"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>`;
      btn.addEventListener("click", () => { if (!this.transitioning) this._showNode(nid, true); });
      this.navContainer.appendChild(btn);
    }
  }

  private _goSequential(dir: number): void {
    if (this.transitioning || this.sortedNodeIds.length === 0) return;
    const idx = this.sortedNodeIds.indexOf(this.currentNodeId);
    const next = idx + dir;
    if (next < 0 || next >= this.sortedNodeIds.length) return;
    const nodeId = this.sortedNodeIds[next];
    this._showNode(nodeId, true);

    const pos = this.nodes.get(nodeId)?.position;
    if (pos) this.onEnter?.(nodeId, pos);
  }

  private _initDrag(): void {
    let startX = 0, startW = 0;
    const onMove = (e: MouseEvent) => { const maxW = this.panel.parentElement!.clientWidth * 0.7; const w = Math.max(MIN_PANEL_W, Math.min(maxW, startW + (e.clientX - startX))); this.panel.style.width = `${w}px`; this._applyCanvasInset(); this.onPanelResize?.(w); };
    const onUp = () => { this.dragHandle.classList.remove("dragging"); document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    this.dragHandle.addEventListener("mousedown", (e) => { e.preventDefault(); startX = e.clientX; startW = this.panel.offsetWidth; this.dragHandle.classList.add("dragging"); document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp); });
  }

  private _applyCanvasInset(): void { this.canvasContainer.style.left = `${this.panel.offsetWidth}px`; }

  // ── Panorama exit handling ───────────────────────────────────────────────────

  private _handlePanoExit(): void {
    if (this.active) {
      // Panel was open before pano → reopen it
      this.panel.classList.add("open");
      this._applyCanvasInset();
    }
    this.onPanoExit?.();
  }

  // ── Keyboard ─────────────────────────────────────────────────────────────────

  private _onKeyDown(e: KeyboardEvent): void {
    if (this.session?.isActive) return; // session owns keyboard while pano is active
    if (!this.active) return;
    if (e.key === "Escape") { e.stopPropagation(); this.exit(); return; }
    const dir = ({ ArrowRight: [1, 0], d: [1, 0], D: [1, 0], ArrowLeft: [-1, 0], a: [-1, 0], A: [-1, 0], ArrowUp: [0, -1], w: [0, -1], W: [0, -1], ArrowDown: [0, 1], s: [0, 1], S: [0, 1] } as Record<string, [number, number]>)[e.key];
    if (!dir) return;
    e.stopPropagation();
    this._navigatePanelInDirection(dir[0], dir[1]);
  }

  private _navigatePanelInDirection(dx: number, dz: number): void {
    if (this.transitioning) return;
    const neighbors = this.adjacency.get(this.currentNodeId) ?? [];
    if (!neighbors.length) return;
    const cp = this.nodes.get(this.currentNodeId)?.position;
    if (!cp) return;
    const td = new THREE.Vector2(dx, dz).normalize();
    let best = -1, bestDot = -Infinity;
    for (const nid of neighbors) {
      const np = this.nodes.get(nid)?.position;
      if (!np) continue;
      const dot = td.dot(new THREE.Vector2(np.x - cp.x, np.z - cp.z).normalize());
      if (dot > bestDot) { bestDot = dot; best = nid; }
    }
    if (best >= 0 && bestDot > 0) this._showNode(best, true);
  }
}
